import { apiSlice } from "application/api/apiSlice";
import { API_PREFIX } from "data/constants";
import { isUuid } from "data/uuid";

export const propertySlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProperty: builder.query({
      query: (id) => ({
        url:
          API_PREFIX +
          (isUuid(id)
            ? `/property/single/${id}`
            : `/property/single/by-slug/${id}`),
        method: "GET",
      }),
      transformResponse: (response) => ({
        property: {
          ...response.property,
        },
        host: {
          ...response.property.host,
        },
      }),
      providesTags: ["property", "EditingProperty"],
    }),
    getPropertyMultiple: builder.query({
      query: (uuids) => ({
        url: API_PREFIX + `/property/multiple`,
        body: { property_uuids: uuids },
        method: "POST",
      }),
      transformResponse: (response) => ({
        properties: response.properties.map((property) => ({
          ...property,
        })),
      }),
      providesTags: ["property", "EditingProperty"],
    }),
    getPropertyStats: builder.query({
      query: (id) => ({
        url: API_PREFIX + `/property/single/stats/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => ({
        ...response.stats,
      }),
      providesTags: ["property", "EditingProperty"],
    }),
    updateProperty: builder.mutation({
      query: (property) => ({
        url: API_PREFIX + "/property/update",
        method: "PUT",
        body: property,
      }),
      invalidatesTags: ["property"],
    }),
    createProperty: builder.mutation({
      query: (property) => ({
        url: API_PREFIX + "/property/create",
        method: "PUT",
        body: property,
      }),
      invalidatesTags: ["property", "HostPropertiesList"],
    }),
    uploadPropertyCover: builder.mutation({
      query: (formData) => ({
        url: API_PREFIX + "/property/upload/cover",
        method: "POST",
        body: formData,
      }),
    }),
    uploadPropertyAlbumImage: builder.mutation({
      query: (formData) => ({
        url: API_PREFIX + "/property/upload/album-image",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["EditingProperty"],
    }),
    getAllProperties: builder.query({
      query: () => ({
        url: API_PREFIX + "/property/all",
        method: "GET",
      }),
    }),
    editProperty: builder.mutation({
      query: (property) => ({
        url: API_PREFIX + "/property/edit",
        method: "PUT",
        body: property,
      }),
      invalidatesTags: ["EditingProperty"],
    }),
    getAvailablePropertiesByDate: builder.query({
      query: (query) => ({
        url: API_PREFIX + "/property/search",
        method: "POST",
        body: {
          check_in: query.check_in,
          check_out: query.check_out,
          guests: query.guests,
          mood: query.mood,
          latitude: query.latitude,
          longitude: query.longitude,
          bboxNorth: query.bboxNorth,
          bboxSouth: query.bboxSouth,
          bboxEast: query.bboxEast,
          bboxWest: query.bboxWest,
          region: query.region,
          state: query.country,
          trend: query.trend,
        },
      }),
    }),
    getPropertyAvailabilityByDates: builder.query({
      query: (availability) => ({
        url: API_PREFIX + "/property/single/availability",
        method: "POST",
        body: availability,
      }),
    }),
    getPropertyAvailabilityByRange: builder.query({
      query: (availability) => ({
        url: API_PREFIX + "/property/single/availability/range",
        method: "POST",
        body: availability,
      }),
    }),
    stopProperty: builder.query({
      query: (property_uuid) => ({
        url: API_PREFIX + "/property/stop",
        method: "POST",
        body: property_uuid,
      }),
    }),
    getAmenitiesListing: builder.query({
      query: () => ({
        url: API_PREFIX + "/amenity/all",
        method: "GET",
      }),
    }),
    getPropertiesByHost: builder.query({
      query: (host_uuid) => ({
        url: API_PREFIX + `/property/host/${host_uuid}`,
        method: "GET",
      }),
      providesTags: ["HostPropertiesList"],
    }),
    getAveragePrice: builder.query({
      query: (city) => ({
        url: API_PREFIX + `/property/averagePrice/${city}`,
        method: "GET",
      }),
    }),
    getHighlights: builder.query({
      query: () => ({
        url: API_PREFIX + `/property/highlights/`,
        method: "GET",
      }),
    }),
    deleteProperty: builder.mutation({
      query: (property_uuid) => ({
        url: API_PREFIX + `/property/${property_uuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EditingProperty"],
    }),

    duplicateProperty: builder.mutation({
      query: (arg) => {
        const property_uuid = typeof arg === "string" ? arg : arg?.property_uuid;
        return {
          url: API_PREFIX + `/property/duplicate/${property_uuid}`,
          method: "POST",
          body: {},
        };
      },
      invalidatesTags: ["Property", "EditingProperty"],
    }),
  }),
});

export const {
  useGetPropertyQuery,
  useLazyGetPropertyQuery,
  useGetPropertyMultipleQuery,
  useLazyGetPropertyMultipleQuery,
  useUpdatePropertyMutation,
  useCreatePropertyMutation,
  useUploadPropertyCoverMutation,
  useUploadPropertyAlbumImageMutation,
  useGetAllPropertiesQuery,
  useEditPropertyMutation,
  useGetAvailablePropertiesByDateQuery,
  useLazyGetAvailablePropertiesByDateQuery,
  useLazyGetPropertyStatsQuery,
  useGetPropertyStatsQuery,
  useGetAmenitiesListingQuery,
  useGetPropertyAvailabilityByDatesQuery,
  useLazyGetPropertyAvailabilityByDatesQuery,
  useLazyGetPropertyAvailabilityByRangeQuery,
  useLazyGetAllPropertiesQuery,
  useGetPropertiesByHostQuery,
  useLazyGetPropertiesByHostQuery,
  useLazyGetAveragePriceQuery,
  useLazyGetHighlightsQuery,
  useGetHighlightsQuery,
  useDeletePropertyMutation,
  useDuplicatePropertyMutation,
} = propertySlice;
